import React, { useEffect } from 'react';

import Axios from 'axios';
import { Field, Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { StringParam, useQueryParam } from 'use-query-params';

import { setNewPassword } from 'api/auth';
import Button from 'components/shared/Button';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import { openMobileAppInfoModal } from 'components/shared/MobileAppInfo';
import AuthTitle from 'components/shared/Typography/AuthTitle';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { showErrorSnackbar, showDefaultSnackbar } from 'hooks/useSnackbar';
import useValidationSchema from 'hooks/useValidationSchema';
import { showMobileInfoSelector } from 'store/app/selectors';
import { getMeThunk } from 'store/user/thunks';
import { setToken } from 'utils/accessToken';
import { setAuthToken } from 'utils/axios';
import logger from 'utils/logger';

import AuthLabel from '../components';
import { SUCCESS_MESSAGE, EXPIRED_LINK_ERROR_MESSAGE } from './constants';
import { validationSchema } from './validationSchema';

function ResetPassword() {
  const dispatch = useDispatch();
  const { token } = useParams();
  const validate = useValidationSchema(validationSchema);
  const [redirectUri] = useQueryParam('redirect_uri', StringParam);
  const showMobileInfo = useSelector(showMobileInfoSelector);

  const handleSubmit = async ({ password, confirmPassword } = {}) => {
    try {
      const data = await setNewPassword({ key: token, password, confirmPassword });
      setAuthToken(data.accessToken);
      setToken(data);
      showDefaultSnackbar(SUCCESS_MESSAGE);

      if (redirectUri) {
        return (location.href = redirectUri);
      }

      dispatch(getMeThunk());
    } catch (err) {
      if (Axios.isAxiosError(err)) {
        if (err.response.status === 401) {
          showErrorSnackbar(EXPIRED_LINK_ERROR_MESSAGE);
          return;
        }

        if (err.response.status === 422) {
          const fieldError = err.response?.data?.fieldError;
          if (Array.isArray(fieldError) && fieldError.length >= 2) {
            return { [fieldError[0]]: fieldError[1] };
          }
        }
      }

      logger.error(err);
      showErrorSnackbar(GENERAL_ERROR_MESSAGE);
    }
  };

  useEffect(() => {
    if (showMobileInfo) {
      const parts = redirectUri?.split('/') || [];
      const deepLinkPath = redirectUri
        ? `reset-password?key=${token}&redirectGpAccessToken=${parts[parts.length - 1]}`
        : `reset-password?key=${token}`;

      openMobileAppInfoModal(deepLinkPath);
    }
  }, [token, showMobileInfo, redirectUri]);

  return (
    <Form onSubmit={handleSubmit} validate={validate}>
      {({ handleSubmit, submitting, hasValidationErrors }) => {
        return (
          <form onSubmit={handleSubmit}>
            <AuthTitle>Set password to get started</AuthTitle>

            <AuthLabel>Set Password</AuthLabel>
            <Field
              component={FormGroupAdapter}
              name="password"
              placeholder="Password"
              type="password"
              withIcon
              withPasswordRequirementsTip
            />

            <AuthLabel>Confirm Password</AuthLabel>
            <Field
              component={FormGroupAdapter}
              name="confirmPassword"
              placeholder="Confirm Password"
              type="password"
              withIcon
            />

            <FormActions isRight>
              <Button htmlType="submit" disabled={submitting || hasValidationErrors}>
                Submit
              </Button>
            </FormActions>
          </form>
        );
      }}
    </Form>
  );
}

export default ResetPassword;
